import Modal from '../components/Modal/Modal'
import Header from '../components/Header/Header'
import HistoryTable from '../components/HistoryTable/HistoryTable'
import PopupMessage from '../components/PopupMessage/PopupMessage'
import Loading from '../components/Loading/Loading'
import StatusText from '../components/StatusText/StatusText'
import HistoryItem from '../components/mobile-components/HistoryItem/HistoryItem'
import MoreButton from '../components/TableButtons/MoreButton'

import { useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import { AuthContext } from '../contexts/AuthContext'
import { formatDate, sortByDate } from '../helpers/formatters'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import useAuth from '../hooks/useAuth'
import useMediaQuery from '../hooks/useMediaQuery'
import {
    getBalanceRequestsOfUser,
    createBalanceRequest,
} from '../api/balance-requests'
import { IoInformationCircleSharp } from 'react-icons/io5'

const BalanceRequest = () => {
    useEffect(() => {
        document.title =
            'Αίτηση Υπολοίπου - Πλατφόρμα Ηλ. Πληρωμών Δήμου Καρπάθου'
    }, [])

    const isMobile = useMediaQuery('(max-width:1024px)')
    const [popup, setPopup] = useState({ message: '', type: '', shown: false })
    const { token } = useContext(AuthContext)
    const { getCurrentUserID } = useAuth()
    const userId = getCurrentUserID()
    const options = {
        headers: {
            Authorization: `Bearer ${token.access_token}`,
        },
    }
    const queryClient = useQueryClient()

    const { isLoading, data } = useQuery({
        queryKey: ['balanceRequestsHistory', userId, options],
        queryFn: () => getBalanceRequestsOfUser(userId, options),
    })

    const toggleHydrometerInfo = () => {
        setPopup(values => ({
            ...values,
            shown: true,
            message: (
                <>
                    <p className="text-sm">
                        Στην περίπτωση που εκπροσωπείτε προμηθευτή του Δήμου
                        μπορείτε να επιλέξετε την σχετική ιδιότητα για να
                        εμφανιστούν στοιχεία που σας ενδιαφέρουν όπως Υπόλοιπο,
                        Τιμολόγια που δεν έχουν εξοφληθεί , Εξοφλημένα τιμολόγια
                        του έτους και Σχετικό Ενταλμα για εκκρεμείς πληρωμές
                    </p>
                </>
            ),
            type: 'image',
        }))
    }

    const mutation = useMutation({
        mutationFn: () =>
            createBalanceRequest({
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token.access_token}`,
                },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['balanceRequestsHistory'],
            })

            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <p>
                        Η αίτηση σας έχει υποβληθεί. Θα ενημερωθείτε σύντομα για
                        την εξέλιξη της με σχετικό e-mail, καθώς και από τον
                        πίνακα &nbsp;
                        {/* Add space with html entity because linter does not let us add with text */}
                        <Link className="font-bold" to={'/balance-requests'}>
                            &quot;Αιτήσεις Υπολοίπου&quot;
                        </Link>
                    </p>
                ),
                type: 'success',
            }))
        },
        onError: () => {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        <p>Η αίτησή σας δεν υποβλήθηκε.</p>
                        <p>Παρακαλώ προσπαθήστε ξανά</p>
                    </>
                ),
                type: 'error',
            }))
        },
    })

    if (isLoading) {
        return <Loading />
    }

    const headings = [
        'Αριθμός Ηλεκτρονικής Αίτησης Υπολοίπου',
        'Ημερομηνία Υποβολής Αίτησης',
        'Κατάσταση',
        'Περισσότερα',
    ]

    const rows = data?.balanceRequests.map(request => {
        return {
            0: request.title ?? '-',
            1: formatDate(request.createdAt) ?? '-',
            2: (
                <StatusText
                    key={3}
                    kind="balance-requests"
                    status={request.status}
                />
            ),
            3:
                request.status === 'COMPLETED' ? (
                    <Link
                        to={`/balance-requests/${request.id}`}
                        className="font-bold text-accent"
                    >
                        Προβολή Ταυτότητας Πληρωμής
                    </Link>
                ) : (
                    <MoreButton
                        key={4}
                        kind={'balance-requests'}
                        requestID={request.id}
                    />
                ),
        }
    })

    // Sort rows with time value
    const sortedRows = sortByDate(rows, 1)

    return (
        <>
            {popup.shown && (
                <PopupMessage
                    message={popup.message}
                    type={popup.type}
                    closePopUp={() =>
                        setPopup(values => ({ ...values, shown: false }))
                    }
                    showCloseButton={true}
                />
            )}

            {isMobile ? (
                <>
                    <h2 className="text-primary text-2xl text-center font-light mt-12 mb-6">
                        Αίτηση Υπολοίπου
                    </h2>

                    <div className="flex flex-wrap gap-5">
                        <label className="flex items-center gap-2 text-sm text-primary font-medium">
                            <input
                                type="checkbox"
                                className="w-4 h-4 accent-[#580404]"
                            />
                            Είμαι προμηθευτής
                        </label>

                        <button
                            type="button"
                            className="flex-shrink-0 h-fit w-fit"
                            onClick={toggleHydrometerInfo}
                        >
                            <IoInformationCircleSharp className="text-accent text-2xl hover:text-primary transition" />
                        </button>
                    </div>

                    <div className="flex justify-between bg-white flex-col items-center gap-12 py-8 my-5 px-10 shadow-lg rounded-2xl">
                        <p className=" text-base text-primary text-center font-bold">
                            Μπορείς να ζητήσεις ενημέρωση για το υπόλοιπό σου με
                            ένα κλικ! Κατά την πρώτη σύνδεση σου, η οικονομική
                            υπηρεσία θα τακτοποιήσει τα σχετικά στοιχεία.
                        </p>

                        <button
                            className="bg-[#580404] hover:bg-white text-white hover:text-[#580404] border border-[#580404] transition-all font-bold rounded-lg py-3.5 px-5"
                            onClick={() => mutation.mutate()}
                            disabled={mutation.isLoading}
                        >
                            Αίτηση Υπολοίπου
                        </button>
                    </div>

                    <h2 className="text-primary text-3xl font-light mt-24 mb-6 text-center">
                        Ιστορικό Αιτήσεων
                    </h2>

                    <p className="w-full max-w-[70%] mx-auto my-8 text-center">
                        {`Μόλις η κατάσταση μιας αίτησης γίνει "Ολοκληρωμένο"
                        πατώντας το κουμπί στη στήλη "Περισσότερα" μπορείς να
                        δεις αν έχεις κάποια οφειλή και να την πληρώσεις άμεσα.`}
                    </p>

                    {data.balanceRequests.length > 0 ? (
                        data.balanceRequests.map((request, index) => {
                            return (
                                <HistoryItem
                                    key={request.id}
                                    headings={headings}
                                    rows={sortedRows[index]}
                                />
                            )
                        })
                    ) : (
                        <h2 className="text-base text-primary font-bold text-center">
                            Δεν βρέθηκαν αιτήσεις
                        </h2>
                    )}
                </>
            ) : (
                <Modal classes="w-full max-w-7xl flex flex-col">
                    <Header
                        includeLogo={true}
                        withBorder={true}
                        includeButtons={true}
                    />

                    <div className="flex flex-col justify-start items-center grow p-6">
                        <h2 className="text-primary text-3xl font-light mt-12 mb-6">
                            Αίτηση Υπολοίπου
                        </h2>

                        <div className="flex flex-wrap gap-5">
                            <label className="flex items-center gap-2 text-sm text-primary font-medium">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-[#580404]"
                                />
                                Είμαι προμηθευτής
                            </label>

                            <button
                                type="button"
                                className="flex-shrink-0 h-fit w-fit"
                                onClick={toggleHydrometerInfo}
                            >
                                <IoInformationCircleSharp className="text-accent text-2xl hover:text-primary transition" />
                            </button>
                        </div>

                        <div className="flex justify-between items-center gap-12 py-8 px-10 shadow-lg rounded-2xl">
                            <div className="flex flex-col gap-4">
                                <p className="font-medium text-base text-primary">
                                    Μπορείς να ζητήσεις ενημέρωση για το
                                    υπόλοιπό σου με ένα κλικ!
                                </p>
                            </div>

                            <button
                                className="bg-accent hover:bg-white text-white hover:text-accent border border-accent transition-all font-bold rounded-lg py-3.5 px-5"
                                onClick={() => mutation.mutate()}
                                disabled={mutation.isLoading}
                            >
                                Αίτηση Υπολοίπου
                            </button>
                        </div>

                        <h2 className="text-primary text-3xl font-light mt-24 mb-6">
                            Ιστορικό Αιτήσεων
                        </h2>
                        <p className="w-full max-w-[70%] mx-auto my-4 text-center">
                            {`Μόλις η κατάσταση μιας αίτησης γίνει "Ολοκληρωμένο"
                        πατώντας το κουμπί στη στήλη "Περισσότερα" μπορείς να
                        δεις αν έχεις κάποια οφειλή και να την πληρώσεις άμεσα.`}
                        </p>

                        <HistoryTable
                            headings={headings}
                            rows={sortedRows}
                            noDataText="Δεν βρέθηκαν αιτήσεις"
                        />
                    </div>
                </Modal>
            )}
        </>
    )
}

export default BalanceRequest
